/**
 * What a customer agrees to at the moment they put a site on the internet.
 *
 * ⚠️ **THE WORDS AND THE VERSION ARE ONE DECLARATION, AND THE VERSION IS THE HALF THAT MATTERS.**
 * "They agreed" is worthless without "to what": the wording of a statement like this changes — it is
 * product copy, it gets shortened, it gets a clause added the first time something goes wrong — and a
 * record that stores only a timestamp cannot say which version of it was on screen. So the API
 * records `version` and the wizard renders `en`/`ar` from the same constant, in a package both
 * import (§0.5).
 *
 * ⚠️ AND IT LIVES IN `@ksa/model` RATHER THAN IN THE API OR THE APP, for the reason the price does:
 * the browser has to show it and the server has to record it, and the day those two disagree is the
 * day the record says somebody agreed to something they never read.
 *
 * ⚠️ IT IS A STATEMENT ABOUT **THE PUBLISHER**, NOT A TERMS-OF-SERVICE ACCEPTANCE. It asks the one
 * question the risk scan (CLAUDE.md §5.12) cannot answer from the copy — whether the person pressing
 * the button is entitled to publish as this business — and it asks it *before* the machine guesses.
 * That order is deliberate: a customer who has said "this is my business" and a page carrying a
 * bank's name is a claim somebody made, where the same page with nothing asked is only ever our
 * inference.
 */

/** Bumped whenever the wording below changes. Old records keep the version they were given. */
export const CONSENT_VERSION = 'publish-1'

export const PUBLISH_CONSENT = {
  version: CONSENT_VERSION,
  /**
   * ⚠️ THREE CLAUSES, AND NO MORE. A consent nobody reads is a consent that proves nothing, and the
   * way to make one unreadable is to make it thorough. Each clause here is a thing that has an
   * actual consequence: who you are, what the site may not pretend to be, and that a stranger can
   * report it.
   *
   * ⚠️ AND THE THIRD CLAUSE IS A **DISCLOSURE**, WHICH IS WHY IT IS IN THE CONSENT RATHER THAN IN A
   * FOOTNOTE. Some sites are asked for a subscription instead of the free week (§5.12), and the only
   * fair moment to say so is before the button — a customer who learns it from a refusal learns it
   * as an accusation.
   */
  en: [
    'This is my business, or I am authorised to publish this site for it.',
    'It does not present itself as a bank, a government service, or an organisation it is not.',
    'Anyone who visits it can report it, and sites that read as one of the above are asked for a subscription rather than the free week.',
  ],
  ar: [
    'هذا نشاطي التجاري، أو لديّ تفويض بنشر هذا الموقع نيابة عنه.',
    'ولا يقدّم نفسه على أنه بنك أو جهة حكومية أو مؤسسة ليست هو.',
    'ويمكن لأي زائر الإبلاغ عنه، والمواقع التي تبدو كذلك يُطلب لها اشتراك بدلًا من الأسبوع المجاني.',
  ],
} as const

export type PublishConsent = typeof PUBLISH_CONSENT
